import { useEffect, useMemo, useRef, useState } from "react";
import { globalStyles } from "./styles/globalStyles";
import { PROFILE, SECTIONS, scrollTo } from "./data/constants";

import Sidebar        from "./components/Sidebar";
import TabBar         from "./components/TabBar";
import Hero           from "./components/Hero";
import Projects       from "./components/Projects";
import Experience     from "./components/Experience";
import About          from "./components/About";
import Skills         from "./components/Skills";
import Certifications from "./components/Certifications";
import Contact        from "./components/Contact";
import { IconBranch, IconCheck, IconSpark } from "./components/icons";

const LINE_H = 22;   // must match --lh
const GUTTER_TOP = 52;

/* ── Which section is under the viewport ─────────────────────────────────── */
function useActiveSection() {
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    // A thin scan-line band across the upper viewport: whichever section crosses
    // it is active. Ratio-based picking loses on sections taller than the screen,
    // which made the tab and outline update late and read as lag.
    const crossing = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => crossing.set(e.target.id, e.isIntersecting));
        const hit = SECTIONS.find(({ id }) => crossing.get(id));
        if (hit) setActive(hit.id);
      },
      { rootMargin: "-26% 0px -66% 0px", threshold: 0 }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return active;
}

/* ── Staggered entry. Writes the class directly — no re-render per element ── */
function useReveal() {
  useEffect(() => {
    const nodes = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add("in");
          obs.unobserve(e.target);       // fire once, then stop paying for it
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.08 }
    );
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);
}

/* ── Line-number gutter + the current-line highlight that tracks scroll ──── */
function useGutter(editorRef, highlightRef) {
  const [lineCount, setLineCount] = useState(0);
  const editorTop = useRef(0);   // editor offset in document space, re-read only on resize

  // Count is driven by the editor's real height, so numbers always reach the end.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setLineCount(Math.ceil(entry.target.scrollHeight / LINE_H));
      editorTop.current = entry.target.getBoundingClientRect().top + window.scrollY;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [editorRef]);

  // Highlight moves by transform only, set imperatively — scrolling never re-renders
  // React and never reads layout, so no forced reflow lands in the scroll frame.
  useEffect(() => {
    let frame = 0;
    const move = () => {
      frame = 0;
      const bar = highlightRef.current;
      if (!bar) return;
      const offset = window.scrollY + window.innerHeight * 0.4 - editorTop.current - GUTTER_TOP;
      const line = Math.max(0, Math.floor(offset / LINE_H));
      bar.style.transform = `translate3d(0, ${line * LINE_H + GUTTER_TOP}px, 0)`;
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(move); };

    move();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [editorRef, highlightRef]);

  return lineCount;
}

/* ── Local clock, isolated so the minute tick doesn't re-render the page ─── */
function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const time = new Intl.DateTimeFormat("en-CA", {
    hour: "numeric", minute: "2-digit", timeZone: "America/Toronto",
  }).format(now);

  return <span>{PROFILE.location} · {time}</span>;
}

export default function App() {
  const editorRef    = useRef(null);
  const highlightRef = useRef(null);

  const active    = useActiveSection();
  const lineCount = useGutter(editorRef, highlightRef);
  useReveal();

  // Static list — memoised so the gutter is not rebuilt on unrelated renders.
  const lines = useMemo(
    () => Array.from({ length: lineCount }, (_, i) => <span key={i}>{i + 1}</span>),
    [lineCount]
  );

  return (
    <>
      <style>{globalStyles}</style>
      <div className="grain" aria-hidden="true" />

      <a className="skip-link" href="#hero">Skip to content</a>

      {/* ── Window title bar ───────────────────────────────────────────── */}
      <header className="titlebar">
        <div className="titlebar-dots" aria-hidden="true">
          <span className="dot" style={{ background: "#c4635c" }} />
          <span className="dot" style={{ background: "#c9a25c" }} />
          <span className="dot" style={{ background: "#6d9b62" }} />
        </div>
        <span className="titlebar-greet">
          welcome — <b>{PROFILE.name.toLowerCase().replace(" ", "-")}</b>/portfolio
        </span>
        <div className="titlebar-right">
          <span className="availability">
            <span className="pulse" />
            {PROFILE.seeking}
          </span>
          <span className="hide-xs"><Clock /></span>
        </div>
      </header>

      {/* ── Shell ──────────────────────────────────────────────────────── */}
      <div className="ide">
        <aside className="rail rail-left" aria-label="Profile">
          <Sidebar />
        </aside>

        <div className="editor" ref={editorRef}>
          <div className="gutter" aria-hidden="true">{lines}</div>
          <div className="line-highlight" ref={highlightRef} aria-hidden="true" />

          <TabBar active={active} />

          <main id="content" className="editor-body">
            <Hero />
            <Experience />
            <Projects />
            <About />
            <Skills />
            <Certifications />
            <Contact />
          </main>
        </div>

        <nav className="rail rail-right" aria-label="Page outline">
          <p className="outline-title">Outline</p>
          {SECTIONS.map(({ id, outline }) => (
            <button
              key={id}
              className={`outline-link${active === id ? " active" : ""}`}
              aria-current={active === id ? "true" : undefined}
              onClick={() => scrollTo(id)}
            >
              {outline}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Status bar ─────────────────────────────────────────────────── */}
      <footer className="statusbar">
        <span className="statusbar-item"><IconBranch width={12} height={12} /> main</span>
        <span className="statusbar-item hide-xs"><IconCheck width={12} height={12} /> 0 problems</span>
        <div className="statusbar-right">
          <a className="statusbar-item" href={PROFILE.github} target="_blank" rel="noreferrer">GitHub</a>
          <a className="statusbar-item" href={PROFILE.linkedin} target="_blank" rel="noreferrer">LinkedIn</a>
          <span className="statusbar-item hide-xs"><IconSpark width={12} height={12} /> React · CRA</span>
        </div>
      </footer>
    </>
  );
}
